import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Handshake as HandshakeIcon } from "lucide-react";

export default function Handshake() {
  const [committed, setCommitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { updateUser } = useAuth();
  const navigate = useNavigate();

  const sign = async () => {
    if (!committed) {
      toast.error("You must commit to proceed.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/handshake/sign", { committed: true });
      updateUser({ handshake_signed: true });
      toast.success("Handshake sealed. Welcome to the Academy.");
      navigate("/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not seal handshake");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#060B14] relative overflow-hidden flex items-center justify-center p-6">
      {/* Background jet image */}
      <div className="absolute inset-0">
        <img
          src="https://images.pexels.com/photos/37106376/pexels-photo-37106376.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt=""
          className="w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#060B14] via-[#060B14]/85 to-[#060B14]" />
      </div>

      <div className="absolute top-6 left-6 z-20"><Logo /></div>

      <div className="relative z-10 w-full max-w-3xl fade-up">
        <div className="glass-strong rounded-sm overflow-hidden">
          {/* Architect video */}
          <div className="aspect-video bg-black relative">
            <video
              data-testid="handshake-architect-video"
              src="https://customer-assets.emergentagent.com/wingman/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/attachments/ba4c66bf9d9648929f4a73249475c459_30day_handshake.mp4"
              controls
              autoPlay
              muted
              loop
              className="w-full h-full object-cover"
            />
            <div className="absolute top-4 left-4 flex items-center gap-2 bg-[#060B14]/80 backdrop-blur px-3 py-1.5 rounded-sm">
              <div className="w-1.5 h-1.5 rounded-full bg-[#F43F5E] light-pulse" />
              <span className="hud-label text-[9px] text-white">Architect · Briefing</span>
            </div>
          </div>

          <div className="p-8 sm:p-12">
            <div className="flex items-center gap-3 mb-6">
              <HandshakeIcon size={20} className="text-gold" />
              <span className="hud-label">The 7-Hour Handshake</span>
            </div>

            <h1 className="font-display text-3xl sm:text-4xl text-white font-light mb-4 leading-tight">
              A commitment to <span className="text-gold italic">your future</span>.
            </h1>

            <p className="text-slate-300 leading-relaxed mb-8 max-w-2xl">
              The Architect has briefed you. The Academy is not for spectators — it's for operators
              who agree to invest <span className="text-gold">seven hours every week</span> into
              their future activation. This handshake is your covenant.
            </p>

            <label className="flex items-start gap-4 p-5 border border-white/10 rounded-sm cursor-pointer hover:border-gold transition-colors" data-testid="handshake-commitment-row">
              <input
                type="checkbox"
                checked={committed}
                onChange={(e) => setCommitted(e.target.checked)}
                data-testid="handshake-checkbox"
                className="mt-1 w-5 h-5 accent-[#D4AF37] cursor-pointer"
              />
              <div>
                <div className="font-display text-white text-base mb-1">
                  I commit to 7 hours of Future Activation every week.
                </div>
                <div className="text-xs text-slate-500">
                  Signing this handshake unlocks your Pilot Dashboard and starts the clock.
                </div>
              </div>
            </label>

            <button
              onClick={sign}
              disabled={!committed || loading}
              data-testid="handshake-sign-button"
              className="btn-gold mt-8 w-full sm:w-auto px-10 py-4 rounded-sm flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {loading ? "Sealing..." : "Sign & Enter Academy"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
